import React, { useCallback, useEffect, useState } from "react";
import EditModal from "../modal/EditModal";
import DeleteModal from "../modal/DeleteModal";
import DataTable from "../reuseable/DataTable";
import ToastMessage from "../reuseable/ToastMessage";
import PaginationControls from "../reuseable/PaginationControls";
import TableSkeletonRow from "../reuseable/TableSkeletonRow";
import useUserManagement from "../../hooks/useUserManagement.js";

const UserManagement = () => {
  const { fetchUsers, deleteUserById, updateUserById } = useUserManagement();

  const [sampleUsers, setSampleUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [userPage, setUserPage] = useState(1);
  const usersPerPage = 10;
  const [userSearch, setUserSearch] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const users = await fetchUsers();
      setSampleUsers(users);
    } catch (e) {
      console.error("User fetch error", e);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = sampleUsers.filter(
    (user) =>
      user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.role.toLowerCase().includes(userSearch.toLowerCase()) ||
      (user.toolAccess || []).some((g) =>
        g.toLowerCase().includes(userSearch.toLowerCase())
      )
  );

  const userTotalPages = Math.ceil(filteredUsers.length / usersPerPage) || 1;
  const paginatedUsers = filteredUsers.slice(
    (userPage - 1) * usersPerPage,
    userPage * usersPerPage
  );
  const tableRows = paginatedUsers.map((user, idx) => ({
    ...user,
    _globalIndex: (userPage - 1) * usersPerPage + idx,
  }));

  const formatCreatedAt = (value) => {
    if (!value) return "-";

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return value;

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const userColumns = [
    {
      key: "email",
      label: "Email",
      cellStyle: { fontWeight: "500", color: "#333" },
      render: (row) => row.email,
    },
    {
      key: "role",
      label: "Role",
      cellStyle: { color: "#666" },
      render: (row) => (
        <span
          style={{
            background: row.role === "Admin" ? "#ffebee" : "#e3f2fd",
            color: row.role === "Admin" ? "#c62828" : "#1565c0",
            padding: "4px 12px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "600",
            display: "inline-block",
          }}
        >
          {row.role}
        </span>
      ),
    },
    {
      key: "createdBy",
      label: "Created By",
      cellStyle: { color: "#666" },
      render: (row) => row.createdBy || row.created_by || row.assignedBy || "-",
    },
    {
      key: "createdAt",
      label: "Created At",
      cellStyle: { color: "#666" },
      render: (row) => formatCreatedAt(row.createdAt || row.created_at),
    },
    {
      key: "actions",
      label: "Actions",
      headerStyle: { width: "10%", textAlign: "left" },
      cellStyle: { textAlign: "left" },
      render: (row) => (
        <div className="flex gap-2 justify-start">
          <button
            type="button"
            onClick={() => {
              setShowEditModal(true);
              setEditData(row);
            }}
            className="inline-block rounded-md border border-[#7d1fa3]/25 bg-white px-3 py-1 text-sm font-semibold text-[#7d1fa3] hover:border-[#E12AFB]/70"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => {
              setShowDeleteModal(true);
              setDeleteIndex(row._globalIndex);
            }}
            className="inline-block rounded-md border border-red-300/80 bg-white px-3 py-1 text-sm font-semibold text-red-700 hover:border-red-500"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const handleDelete = async () => {
    if (deleteIndex === null || deleteIndex === undefined) return;

    try {
      const user = filteredUsers[deleteIndex];

      if (!user?.id) {
        setToast({ type: "error", message: "❌ Unable to find selected user" });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      setIsDeleting(true);
      await deleteUserById(user.id);

      setToast({ type: "success", message: "✅ User deleted successfully!" });
      setTimeout(() => setToast(null), 3000);

      setShowDeleteModal(false);
      setDeleteIndex(null);
      await loadUsers();
    } catch (err) {
      console.error("Delete error", err);
      const errorMsg = err?.response?.data?.message || "Failed to delete user";
      setToast({ type: "error", message: `❌ ${errorMsg}` });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  const updateUser = async (formData) => {
    try {
      await updateUserById(editData.id, {
        role: formData.role,
        toolAccess: formData.toolAccess,
      });
      await loadUsers();
      setShowEditModal(false);
    } catch (err) {
      console.error("Update failed", err);
      alert("Failed to update user");
    }
  };

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          padding: 32,
          margin: "0 auto",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Search for Email, Assigned By"
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value);
              setUserPage(1);
            }}
            disabled={isLoading}
            style={{
              padding: 8,
              width: 300,
              borderRadius: 4,
              border: "1px solid #ccc",
              marginBottom: 8,
              backgroundColor: isLoading ? "#f0f0f0" : "white",
              cursor: isLoading ? "not-allowed" : "auto",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 4,
              alignContent: "center",
              marginTop: 8,
            }}
          >
            <span style={{ fontWeight: "bold" }}>
              Total number of Users {filteredUsers.length}
            </span>
            <PaginationControls
              page={userPage}
              totalPages={userTotalPages}
              isLoading={isLoading}
              onPrev={() => setUserPage((p) => Math.max(1, p - 1))}
              onNext={() => setUserPage((p) => Math.min(userTotalPages, p + 1))}
            />
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <DataTable
            columns={userColumns}
            rows={tableRows}
            loading={isLoading}
            skeletonRowCount={5}
            emptyMessage={<span style={{ color: "#999", fontSize: "15px" }}>📭 No users found</span>}
            rowKey={(row) => row.id || row.email || row._globalIndex}
            rowProps={(_, idx) => ({
              style: {
                background: idx % 2 === 0 ? "#bb1ccc0d" : "#f3f0f7",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                border: "1px solid #e5e5e5",
                transition: "all 0.3s ease",
                cursor: "pointer",
              },
              onMouseEnter: (e) => {
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.15)";
                e.currentTarget.style.transform = "translateY(-2px)";
              },
              onMouseLeave: (e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                e.currentTarget.style.transform = "translateY(0)";
              },
            })}
            loadingRow={(idx) => (
              <TableSkeletonRow
                idx={idx}
                rowKeyPrefix="user-skeleton"
                columnCount={userColumns.length}
                actionColumnIndex={userColumns.length - 1}
                cellWidths={["10rem", "6rem", "7rem", "7rem", "7rem"]}
                actionAlign="left"
                actionJustify="start"
                actionSkeletonCount={2}
              />
            )}
          />
        </div>
      </div>

      {showEditModal && editData && (
        <EditModal
          title="Edit User"
          fields={[
            { label: "Email", key: "email", readOnly: true },
            {
              label: "Role",
              key: "role",
              type: "select",
              options: [
                { label: "Admin", value: "Admin" },
                { label: "Member", value: "Member" },
              ],
            },
          ]}
          data={editData}
          onClose={() => setShowEditModal(false)}
          onSave={(formData) => updateUser(formData)}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          title="Are you sure you want to delete this user?"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isLoading={isDeleting}
        />
      )}

      <ToastMessage toast={toast} />
    </>
  );
};

export default UserManagement;